import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Role } from "@/config/roles";

/** The signed-in principal, as returned by GET /auth/me. */
export type SessionUser = {
  id: string;
  email: string;
  name: string;
  title: string | null;
  role: Role;
  orgId: string | null;
};

type SessionState = {
  status: "unknown" | "authenticated" | "anonymous";
  accessToken: string | null;
  user: SessionUser | null;
  setSession: (input: { accessToken: string; user: SessionUser }) => void;
  setUser: (user: SessionUser) => void;
  clear: () => void;
};

/**
 * Client-side session. The access token is short-lived and refreshed by the
 * API's httpOnly refresh cookie (see lib/api/client.ts). Persisting it lets a
 * PWA reopen without a login round-trip; nothing sensitive beyond the token and
 * the public profile is stored.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      status: "unknown",
      accessToken: null,
      user: null,
      setSession: ({ accessToken, user }) => set({ accessToken, user, status: "authenticated" }),
      setUser: (user) => set({ user }),
      clear: () => set({ accessToken: null, user: null, status: "anonymous" }),
    }),
    {
      name: "egc.session",
      // Only the public profile is written to storage. The access token stays
      // in memory: anything that can read localStorage (an injected script, a
      // browser extension) would otherwise hold a usable credential. A reload
      // starts with a profile and no token, and the API client's first
      // request gets a fresh one from the httpOnly refresh cookie.
      partialize: (s) => ({ user: s.user }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.status = state.user ? "authenticated" : "anonymous";
      },
    },
  ),
);

export const sessionSelectors = {
  isAuthenticated: (s: SessionState) => s.status === "authenticated" && !!s.user,
  role: (s: SessionState) => s.user?.role ?? null,
};

/** Non-hook access for router guards and the API client. */
export const session = {
  get: () => useSessionStore.getState(),
  isAuthenticated: () => sessionSelectors.isAuthenticated(useSessionStore.getState()),
};
